/**
 * How loud an alert looks, and what a run's outcome reads as.
 *
 * Pure and dependency-free so CRA's jest can test it.
 *
 * THE TONES ARE DELIBERATELY QUIET AT THE BOTTOM. Most of what a monitor finds
 * is DIGEST tier — that is the design, not a shortfall — so a feed that painted
 * every row in a colour would be a wall of colour, and the one IMMEDIATE row
 * would not stand out from it. So DIGEST is plain text and only the top two
 * tiers carry any weight at all.
 *
 * And NOTHING IS RED UNLESS SOMETHING IS BROKEN. "Nothing changed" is the normal
 * outcome for a probe, most days, on most subjects. A run list that used red for
 * a quiet week would be red nearly always, and a real failure would be invisible
 * in it — the same argument `WatchRunEndedReason` makes for ending those runs
 * COMPLETED rather than FAILED.
 */
import type { WatchTier } from '../../types';

export interface Tone {
  /** Tailwind classes for the chip. */
  chip: string;
  /** What the chip says. */
  label: string;
  /** Short reason, for a title attribute. */
  hint: string;
}

const TIER_TONES: Record<WatchTier, Tone> = {
  IMMEDIATE: {
    chip:
      'bg-red-100 text-red-800 border border-red-200 ' +
      'dark:bg-red-900/40 dark:text-red-200 dark:border-red-800',
    label: 'Now',
    hint: 'Emailed straight away - this one does not wait for the digest',
  },
  SAME_DAY: {
    chip:
      'bg-amber-100 text-amber-800 border border-amber-200 ' +
      'dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800',
    // Not 'Today': beside an alert from last week it read as the date.
    label: 'Soon',
    hint: 'Worth looking at soon; it goes out in the next digest',
  },
  DIGEST: {
    chip: 'text-gray-500 dark:text-gray-400',
    label: 'Digest',
    hint: 'Worth knowing, not worth interrupting for',
  },
};

const UNKNOWN_TIER: Tone = {
  chip: 'text-gray-500 dark:text-gray-400',
  label: 'Digest',
  hint: 'Worth knowing, not worth interrupting for',
};

/** An unknown tier reads as the quietest thing there is — a value this client has
 *  not been taught yet must not shout. */
export function tierTone(tier: string): Tone {
  return TIER_TONES[tier as WatchTier] ?? UNKNOWN_TIER;
}

export type OutcomeTone = 'good' | 'quiet' | 'waiting' | 'bad';

/**
 * What a run's `ended_reason` looks like.
 *
 * `NO_CHANGE` and `BASELINE` are QUIET, not bad. `QUOTA` and `NO_CREDITS` are
 * quiet too: they are a budget, not a fault, and the run genuinely completed.
 */
export function outcomeTone(endedReason: string | null, status: string): OutcomeTone {
  if (!endedReason) {
    return status === 'RUNNING' || status === 'QUEUED' ? 'waiting' : 'quiet';
  }
  switch (endedReason) {
    case 'COMPLETED':
      return 'good';
    case 'NO_CHANGE':
    case 'BASELINE':
    case 'QUOTA':
    case 'NO_CREDITS':
    case 'CANCELLED':
      return 'quiet';
    case 'AWAITING_BROWSER':
      return 'waiting';
    default:
      // UNREACHABLE, ERROR, ABANDONED, MAX_FAILURES.
      return 'bad';
  }
}

export const OUTCOME_CLASSES: Record<OutcomeTone, string> = {
  good: 'text-emerald-700 dark:text-emerald-300',
  quiet: 'text-gray-500 dark:text-gray-400',
  waiting: 'text-blue-600 dark:text-blue-300',
  bad: 'text-red-700 dark:text-red-300',
};

/** The dot beside a subject's name on the list. Mirrors `subjectStatus`'s tones. */
export const STATUS_CLASSES: Record<'problem' | 'attention' | 'idle' | 'ok', string> = {
  problem: 'bg-red-500',
  attention: 'bg-amber-500',
  idle: 'bg-gray-400',
  ok: 'bg-emerald-500',
};

/** A family key to a short word, when the server has not sent its title. */
export function familyLabel(family: string, title?: string | null): string {
  if (title && title.trim()) return title;
  return family
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
